
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useContactDeals = (contactId: string) => {
  return useQuery({
    queryKey: ['contact-deals', contactId],
    queryFn: async () => {
      console.log("Fetching deals for contact:", contactId);
      const { data, error } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(first_name, last_name, email),
          organization:organizations(name, company_id),
          stage:crm_deal_stages(name, color),
          assigned_staff:crm_staff!crm_deals_assigned_to_staff_id_fkey(first_name, last_name, email),
          assigned_partner:crm_partners!crm_deals_assigned_to_partner_id_fkey(first_name, last_name, email)
        `)
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching contact deals:", error);
        throw error;
      }
      
      console.log("Fetched contact deals:", data);
      return data || [];
    },
    enabled: !!contactId,
  });
};

export const useOrganizationDeals = (organizationId: string) => {
  return useQuery({
    queryKey: ['organization-deals', organizationId],
    queryFn: async () => {
      console.log("Fetching deals for organization:", organizationId);
      const { data, error } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(first_name, last_name, email),
          organization:organizations(name, company_id),
          stage:crm_deal_stages(name, color),
          assigned_staff:crm_staff!crm_deals_assigned_to_staff_id_fkey(first_name, last_name, email),
          assigned_partner:crm_partners!crm_deals_assigned_to_partner_id_fkey(first_name, last_name, email)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching organization deals:", error);
        throw error;
      }
      
      console.log("Fetched organization deals:", data);
      return data || [];
    },
    enabled: !!organizationId,
  });
};

export const useStaffDealsAssigned = (staffId: string) => {
  return useQuery({
    queryKey: ['staff-deals-assigned', staffId],
    queryFn: async () => {
      console.log("Fetching deals assigned to staff:", staffId);
      const { data, error } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(first_name, last_name, email),
          organization:organizations(name, company_id),
          stage:crm_deal_stages(name, color),
          assigned_staff:crm_staff!crm_deals_assigned_to_staff_id_fkey(first_name, last_name, email),
          assigned_partner:crm_partners!crm_deals_assigned_to_partner_id_fkey(first_name, last_name, email)
        `)
        .eq('assigned_to_staff_id', staffId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching staff assigned deals:", error);
        throw error;
      }
      
      console.log("Fetched staff assigned deals:", data);
      return data || [];
    },
    enabled: !!staffId,
  });
};

export const usePartnerDealsAssigned = (partnerId: string) => {
  return useQuery({
    queryKey: ['partner-deals-assigned', partnerId],
    queryFn: async () => {
      console.log("Fetching deals assigned to partner:", partnerId);
      const { data, error } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(first_name, last_name, email),
          organization:organizations(name, company_id),
          stage:crm_deal_stages(name, color),
          assigned_staff:crm_staff!crm_deals_assigned_to_staff_id_fkey(first_name, last_name, email),
          assigned_partner:crm_partners!crm_deals_assigned_to_partner_id_fkey(first_name, last_name, email)
        `)
        .eq('assigned_to_partner_id', partnerId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching partner assigned deals:", error);
        throw error;
      }
      
      console.log("Fetched partner assigned deals:", data);
      return data || [];
    },
    enabled: !!partnerId,
  });
};

export const useStaffOwnedContacts = (staffId: string) => {
  return useQuery({
    queryKey: ['staff-owned-contacts', staffId],
    queryFn: async () => {
      console.log("Fetching contacts owned by staff:", staffId);
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          email,
          organization_id,
          organizations(name, company_id)
        `)
        .eq('internal_owner_id', staffId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching staff owned contacts:", error);
        throw error;
      }
      
      console.log("Fetched staff owned contacts:", data);
      return data || [];
    },
    enabled: !!staffId,
  });
};

export const useStaffOwnedContactDeals = (staffId: string) => {
  return useQuery({
    queryKey: ['staff-owned-contact-deals', staffId],
    queryFn: async () => {
      console.log("Fetching deals for contacts owned by staff:", staffId);
      
      // First get contacts owned by this staff member
      const { data: contacts, error: contactsError } = await supabase
        .from('profiles')
        .select('id')
        .eq('internal_owner_id', staffId);

      if (contactsError) {
        console.error("Error fetching staff owned contacts:", contactsError);
        throw contactsError;
      }

      if (!contacts || contacts.length === 0) {
        return [];
      }

      const contactIds = contacts.map(c => c.id);

      // Now get deals for those contacts
      const { data, error } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(first_name, last_name, email),
          organization:organizations(name, company_id),
          stage:crm_deal_stages(name, color),
          assigned_staff:crm_staff!crm_deals_assigned_to_staff_id_fkey(first_name, last_name, email),
          assigned_partner:crm_partners!crm_deals_assigned_to_partner_id_fkey(first_name, last_name, email)
        `)
        .in('contact_id', contactIds)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching deals for staff owned contacts:", error);
        throw error;
      }
      
      console.log("Fetched deals for staff owned contacts:", data);
      return data || [];
    },
    enabled: !!staffId,
  });
};
